import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Dumbbell,
  Users,
  TrendingUp,
  Apple,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching services:", err);
      }
    };
    fetchServices();
  }, []);

  const serviceIcons = {
    "personal-training": Dumbbell,
    groepslessen: Users,
    transformatie: TrendingUp,
    voedingsbegeleiding: Apple,
  };

  const serviceDetails = {
    "personal-training": {
      subtitle: "1-op-1 Begeleiding",
      longDescription:
        "Met personal training krijg je de volledige aandacht van je coach. Elk aspect van je training wordt afgestemd op jouw specifieke doelen, niveau en mogelijkheden. Van warming-up tot cooldown, elke seconde telt.",
      benefits: [
        "100% focus op jouw doelen",
        "Training op jouw tempo",
        "Maximale resultaten in minimale tijd",
        "Flexibele planning",
        "Continue progressie monitoring",
      ],
    },
    groepslessen: {
      subtitle: "Train Samen, Groei Samen",
      longDescription:
        "Onze groepslessen combineren de energie van een team met professionele begeleiding. De groepsdynamiek motiveert je om harder te pushen dan je alleen zou doen, terwijl onze coaches zorgen voor correcte uitvoering.",
      benefits: [
        "Extra motivatie van de groep",
        "Gevarieerde lesinhoud",
        "Sociale connecties",
        "Vaste lesstructuur",
        "Betaalbare optie",
      ],
    },
    transformatie: {
      subtitle: "Complete Verandering",
      longDescription:
        "Een transformatietraject gaat verder dan alleen training. Het is een complete aanpak waarbij we werken aan je lichaam, voeding en mindset. In 12 weken begeleiden we je naar een nieuwe versie van jezelf.",
      benefits: [
        "12-weken intensief programma",
        "Wekelijkse check-ins",
        "Voedingsplan inclusief",
        "Vooruitgang met foto's en metingen",
        "Mindset coaching",
      ],
    },
    voedingsbegeleiding: {
      subtitle: "Voeding is 70% van je Resultaat",
      longDescription:
        "Training alleen is niet genoeg. Onze voedingsbegeleiding helpt je om de juiste keuzes te maken in de keuken. Samen met onze diëtiste ontwikkelen we een plan dat past bij jouw levensstijl en doelen.",
      benefits: [
        "Persoonlijk voedingsschema",
        "Praktische maaltijdtips",
        "Professionele diëtiste begeleiding",
        "Supplementadvies",
        "Duurzame gewoontevorming",
      ],
    },
  };

  return (
    <main data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="services-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Onze Diensten</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Alles Wat Je Nodig Hebt
              <br />
              <span className="text-red-500">Onder Één Dak</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Van personal training tot groepslessen, van voedingsbegeleiding
              tot complete transformaties. Arena Gym biedt alles voor jouw gezondheid
              en fitness.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-[#121212]" data-testid="services-list">
        {services.map((service, index) => {
          const Icon = serviceIcons[service.id] || Dumbbell;
          const details = serviceDetails[service.id] || {
            subtitle: "",
            longDescription: service.description,
            benefits: service.features,
          };
          const isEven = index % 2 === 0;

          return (
            <div
              key={service.id}
              className={`section ${
                isEven ? "bg-[#121212]" : "bg-[#0A0A0A]"
              }`}
              data-testid={`service-detail-${service.id}`}
            >
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                    isEven ? "" : "lg:flex-row-reverse"
                  }`}
                >
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={isEven ? "order-1" : "order-1 lg:order-2"}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <Icon className="w-8 h-8 text-red-500" />
                      <span className="caption">{details.subtitle}</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                      {service.title}
                    </h2>
                    <p className="text-zinc-400 leading-relaxed mb-8">
                      {details.longDescription}
                    </p>

                    <ul className="space-y-3 mb-8">
                      {details.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/boeken"
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid={`service-cta-${service.id}`}
                    >
                      Boek Een Intake
                      <ArrowRight size={18} />
                    </Link>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={isEven ? "order-2" : "order-2 lg:order-1"}
                  >
                    <div className="aspect-[4/3] rounded-sm overflow-hidden border border-zinc-800">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Additional Service - Diëtiste */}
      <section className="section bg-[#121212]" data-testid="dietiste-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] rounded-sm overflow-hidden border border-zinc-800">
                <img
                  src="/voeding-new.webp"
                  alt="Diëtiste Begeleiding"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <Apple className="w-8 h-8 text-red-500" />
                <span className="caption">Professioneel Advies</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
                Diëtiste <span className="text-red-500">Begeleiding</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Naast onze eigen voedingsbegeleiding werken wij samen met een
                gediplomeerde diëtiste. Voor specifieke voedingsvragen,
                allergieën, of medische diëten kun je terecht bij onze
                gespecialiseerde partner.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  "Gediplomeerde diëtiste",
                  "Medische diëten",
                  "Allergie-advies",
                  "Sportvoeding specialisatie",
                  "Vergoeding via zorgverzekering mogelijk",
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{benefit}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="dietiste-cta"
              >
                Neem Contact Op
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#0A0A0A]" data-testid="services-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Klaar om te starten?</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Boek Je <span className="text-red-500">Gratis</span> Intake
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Tijdens de intake bespreken we jouw doelen en bepalen we samen
              welk traject het beste bij je past. Volledig gratis en vrijblijvend.
            </p>
            <Link
              to="/boeken"
              data-testid="services-book-cta"
              className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5"
            >
              Boek Je Intake Nu
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
